// ─── Synthesis Intelligence ───────────────────────────────────────────────────
const synthesisIntelligence = {
  name: 'Synthesis Intelligence',
  status: 'Building',
  role: 'Founder',
  website: 'PLACEHOLDER_SYNTHESIS_URL', // ← replace with your URL
  description: `Synthesis Intelligence is an AI-powered research tool built for financial analysts. It automates the most time-consuming parts of the research process—parsing filings, synthesizing earnings calls, surfacing relevant signals—so analysts can spend less time on information retrieval and more time on insight generation. Built at the intersection of finance and AI, it's the product I wish had existed during my time in consulting.`,
  tools: ['React', 'Node.js', 'Python', 'PostgreSQL', 'OpenAI API', 'FastAPI'],
};

// ─── Pausa ────────────────────────────────────────────────────────────────────
const pausa = {
  name: 'Pausa',
  status: 'Live',
  role: 'Founding Product & Growth Engineer',
  website: 'PLACEHOLDER_PAUSA_URL', // ← replace with your URL (e.g. pausapp.com)
  description: `Pausa is a mental wellness app backed by Forbes 30 Under 30 founders that helps people reconnect with the power of breathwork and mindful pausing. I joined as an unpaid intern and was hired as Founding Engineer three months later. I architected and shipped 6 production systems that contributed to 300% app growth and over 2,000 active users.`,
  tools: ['TypeScript', 'Node.js', 'Express', 'Next.js', 'PostgreSQL', 'REST APIs'],
  systems: [
    {
      name: 'AI Mood Tracker',
      description: 'A conversational AI layer that lets users log their emotional state through natural language. The system parses entries, identifies patterns over time, and surfaces personalized insights — giving users a clearer picture of how stress, sleep, and breathing practices affect their mental state.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL', // ← replace with YouTube embed URL
    },
    {
      name: 'User Analytics Dashboard',
      description: 'An in-app analytics tab that gives users visibility into their own behavior — streak data, session frequency, breathing patterns, and mood trends. Designed to drive engagement by surfacing progress the user might not have noticed on their own.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL',
    },
    {
      name: 'Internal Analytics Dashboard',
      description: 'A real-time internal dashboard for the founding team to monitor growth metrics, retention, DAU/MAU ratios, and feature adoption. Replaced manual spreadsheet tracking and became the team\'s primary decision-making tool.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL',
    },
    {
      name: 'System 4',
      description: 'PLACEHOLDER — Describe this system here.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL',
    },
    {
      name: 'System 5',
      description: 'PLACEHOLDER — Describe this system here.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL',
    },
    {
      name: 'System 6',
      description: 'PLACEHOLDER — Describe this system here.',
      videoUrl: 'PLACEHOLDER_VIDEO_URL',
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const statusColors = {
  'Building': 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  'Live':     'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
};

const isPlaceholder = (url) => !url || url.startsWith('PLACEHOLDER');

const VideoEmbed = ({ url, title }) => {
  if (isPlaceholder(url)) {
    return (
      <div className="w-full aspect-video rounded-xl flex flex-col items-center justify-center gap-3
        dark:bg-slate-800/60 bg-slate-100 border dark:border-slate-700/50 border-slate-200">
        <div className="w-12 h-12 rounded-full flex items-center justify-center
          dark:bg-slate-700 bg-slate-200">
          <svg className="w-5 h-5 dark:text-slate-400 text-slate-500 translate-x-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        <p className="text-xs dark:text-slate-500 text-slate-400 font-medium">Demo video coming soon</p>
      </div>
    );
  }

  return (
    <div className="w-full aspect-video rounded-xl overflow-hidden border dark:border-slate-700/50 border-slate-200">
      <iframe
        src={url}
        title={title}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

const WebsiteLink = ({ url }) => {
  if (isPlaceholder(url)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs
        dark:text-slate-500 text-slate-400 italic">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M14.828 14.828a4 4 0 015.656 0l4-4a4 4 0 01-5.656-5.656l-1.1 1.1" />
        </svg>
        Website coming soon
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
      {url.replace(/^https?:\/\//, '')}
    </a>
  );
};

// ─── Synthesis Card ───────────────────────────────────────────────────────────
const SynthesisCard = () => (
  <div
    className="group relative rounded-2xl overflow-hidden
      dark:border border dark:border-slate-700/50 border-slate-200
      dark:bg-[#0e0c29] bg-white
      dark:hover:border-amber-500/30 hover:border-amber-400/30
      dark:hover:shadow-xl hover:shadow-xl dark:hover:shadow-amber-500/10
      hover:-translate-y-1 transition-all duration-500"
    data-aos="fade-up"
  >
    {/* Top accent */}
    <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600" />

    <div className="p-8 flex flex-col gap-5">
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-bold dark:text-white text-slate-900">
            {synthesisIntelligence.name}
          </h3>
          <p className="text-sm text-amber-400 font-medium">{synthesisIntelligence.role}</p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${statusColors[synthesisIntelligence.status]}`}>
          {synthesisIntelligence.status}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm md:text-base dark:text-slate-300 text-slate-600 leading-relaxed">
        {synthesisIntelligence.description}
      </p>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-4
        border-t dark:border-slate-700/50 border-slate-100">
        <WebsiteLink url={synthesisIntelligence.website} />
        <div className="flex flex-wrap gap-2">
          {synthesisIntelligence.tools.map(tool => (
            <span key={tool} className="text-xs px-2.5 py-1 rounded-lg
              dark:bg-slate-700/50 bg-slate-100
              dark:text-slate-300 text-slate-600
              dark:border border dark:border-slate-600/50 border-slate-200">
              {tool}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Pausa Card ───────────────────────────────────────────────────────────────
const PausaCard = () => (
    <div
      className="relative rounded-2xl overflow-hidden
        dark:border border dark:border-slate-700/50 border-slate-200
        dark:bg-[#0e0c29] bg-white"
      data-aos="fade-up"
      data-aos-delay="100"
    >
      {/* Top accent */}
      <div className="h-1 w-full bg-gradient-to-r from-violet-500 via-blue-500 to-cyan-500" />

      {/* Project header */}
      <div className="p-8 flex flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <h3 className="text-2xl font-bold dark:text-white text-slate-900">{pausa.name}</h3>
            <p className="text-sm text-violet-400 font-medium">{pausa.role}</p>
          </div>
          <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${statusColors[pausa.status]}`}>
            {pausa.status}
          </span>
        </div>

        <p className="text-sm md:text-base dark:text-slate-300 text-slate-600 leading-relaxed">
          {pausa.description}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-4
          border-t dark:border-slate-700/50 border-slate-100">
          <WebsiteLink url={pausa.website} />
          <div className="flex flex-wrap gap-2">
            {pausa.tools.map(tool => (
              <span key={tool} className="text-xs px-2.5 py-1 rounded-lg
                dark:bg-slate-700/50 bg-slate-100
                dark:text-slate-300 text-slate-600
                dark:border border dark:border-slate-600/50 border-slate-200">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Systems divider */}
      <div className="px-8 pb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px dark:bg-slate-700/50 bg-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-widest
            dark:text-slate-400 text-slate-500">
            6 Systems I Shipped
          </span>
          <div className="flex-1 h-px dark:bg-slate-700/50 bg-slate-200" />
        </div>
      </div>

      {/* Systems grid */}
      <div className="px-8 pb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pausa.systems.map((system, i) => (
          <div
            key={i}
            className="flex flex-col gap-4 rounded-xl p-5
              dark:bg-slate-800/40 bg-slate-50
              dark:border border dark:border-slate-700/40 border-slate-200"
            data-aos="fade-up"
            data-aos-delay={i * 60}
          >
            {/* System header */}
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-bold dark:text-white text-slate-900 leading-snug">
                {system.name}
              </h4>
              <span className="text-xs dark:text-slate-500 text-slate-400 shrink-0">
                #{i + 1}
              </span>
            </div>

            {/* System description */}
            <p className="text-xs dark:text-slate-400 text-slate-600 leading-relaxed">
              {system.description}
            </p>

            {/* Video */}
            <VideoEmbed url={system.videoUrl} title={`${system.name} demo`} />
          </div>
        ))}
      </div>
    </div>
);

// ─── Section ──────────────────────────────────────────────────────────────────
const Projects = () => (
  <section id="projects" className="w-full" data-aos="fade-up">

    {/* Section Header */}
    <div className="text-center mb-12">
      <p className="section-eyebrow">What I've Built</p>
      <h2 className="section-heading">Projects</h2>
      <div className="section-divider" />
      <p className="dark:text-slate-400 text-slate-600 text-sm mt-4 max-w-xl mx-auto">
        Real products built to solve real problems — from zero to production.
      </p>
    </div>

    <div className="flex flex-col gap-8">
      <SynthesisCard />
      <PausaCard />
    </div>

  </section>
);

export default Projects;
